import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'https://inteliheads.in/api';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests if it exists
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('adminToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// ==================== ADMIN AUTH APIs ====================

export const adminLogin = async (email, password) => {
  try {
    const response = await api.post('/auth/login', { email, password });
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export const adminLogout = async () => {
  try {
    const response = await api.post('/auth/logout');
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export const verifyAdminToken = async () => {
  try {
    const response = await api.get('/auth/verify');
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export const getCurrentAdmin = async () => {
  try {
    const response = await api.get('/auth/me');
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export const updateCurrentAdmin = async (data) => {
  try {
    const response = await api.put('/auth/me', data);
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export const changePassword = async (id, currentPassword, newPassword) => {
  try {
    const response = await api.post(`/auth/change-password/${id}`, { currentPassword, newPassword });
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

// Admin Management (Super Admin only)
export const getAllAdmins = async () => {
  try {
    const response = await api.get('/auth/admins');
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export const createAdmin = async (adminData) => {
  try {
    const response = await api.post('/auth/admins', adminData);
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export const updateAdmin = async (id, adminData) => {
  try {
    const response = await api.put(`/auth/admins/${id}`, adminData);
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export const deleteAdmin = async (id) => {
  try {
    const response = await api.delete(`/auth/admins/${id}`);
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

// ==================== PUBLIC APIs ====================

export const submitContact = async (formData) => {
  try {
    const response = await api.post('/contact/submit', formData);
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export const enrollTraining = async (formData) => {
  try {
    const response = await api.post('/training/enroll', formData);
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export const submitApplication = async (formData) => {
  try {
    const response = await api.post('/career/apply', formData);
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

// ==================== HERO SLIDE APIs ====================

export const getHeroSlides = async () => {
  try {
    const response = await api.get('/hero/public/slides');
    return response.data;
  } catch (error) {
    console.error('Error fetching hero slides:', error);
    return { success: false, data: [] };
  }
};

export const getAllSlides = async () => {
  try {
    const response = await api.get('/hero');
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export const getSlideById = async (id) => {
  try {
    const response = await api.get(`/hero/${id}`);
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export const createSlide = async (slideData) => {
  try {
    const response = await api.post('/hero', slideData);
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export const updateSlide = async (id, slideData) => {
  try {
    const response = await api.put(`/hero/${id}`, slideData);
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export const deleteSlide = async (id) => {
  try {
    const response = await api.delete(`/hero/${id}`);
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export const toggleSlideStatus = async (id) => {
  try {
    const response = await api.patch(`/hero/${id}/toggle`);
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

// ==================== CATEGORIES APIs ====================

export const getCategories = async () => {
  try {
    const response = await api.get('/categories');
    return response.data;
  } catch (error) {
    console.error('Error fetching categories:', error);
    return { data: [], grouped: {} };
  }
};

export const getCategoriesByType = async (type) => {
  try {
    const response = await api.get(`/categories/type/${type}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching ${type} categories:`, error);
    return { data: [] };
  }
};

export const getPublicCategories = async () => {
  try {
    const response = await api.get('/categories/public');
    return response.data;
  } catch (error) {
    console.error('Error fetching public categories:', error);
    return { data: [], grouped: {} };
  }
};

export const getPublicCategoriesByType = async (type) => {
  try {
    const response = await api.get(`/categories/public/${type}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching public ${type} categories:`, error);
    return { data: [] };
  }
};

export const getCategoryById = async (id) => {
  try {
    const response = await api.get(`/categories/${id}`);
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export const createCategory = async (categoryData) => {
  try {
    const response = await api.post('/categories', categoryData);
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export const updateCategory = async (id, categoryData) => {
  try {
    const response = await api.put(`/categories/${id}`, categoryData);
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export const deleteCategory = async (id) => {
  try {
    const response = await api.delete(`/categories/${id}`);
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export const toggleCategoryStatus = async (id) => {
  try {
    const response = await api.patch(`/categories/${id}/toggle`);
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export const updateCategoryOrder = async (categories) => {
  try {
    const response = await api.put('/categories/order/batch', { categories });
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

// ==================== PRODUCTS APIs ====================

export const getPublicProducts = async () => {
  try {
    const response = await api.get('/cms/products/public');
    return response.data;
  } catch (error) {
    console.error('Error fetching products:', error);
    return { data: [] };
  }
};

export const getProducts = async () => {
  try {
    const response = await api.get('/cms/products');
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export const getProductById = async (id) => {
  try {
    const response = await api.get(`/cms/products/${id}`);
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export const createProduct = async (productData) => {
  try {
    const response = await api.post('/cms/products', productData);
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export const updateProduct = async (id, productData) => {
  try {
    const response = await api.put(`/cms/products/${id}`, productData);
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export const deleteProduct = async (id) => {
  try {
    const response = await api.delete(`/cms/products/${id}`);
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export const toggleProductStatus = async (id) => {
  try {
    const response = await api.patch(`/cms/products/${id}/toggle`);
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

// ==================== SERVICES APIs ====================

export const getPublicServices = async () => {
  try {
    const response = await api.get('/cms/services/public');
    return response.data;
  } catch (error) {
    console.error('Error fetching services:', error);
    return { data: [] };
  }
};

export const getServices = async () => {
  try {
    const response = await api.get('/cms/services');
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export const getServiceById = async (id) => {
  try {
    const response = await api.get(`/cms/services/${id}`);
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export const createService = async (serviceData) => {
  try {
    const response = await api.post('/cms/services', serviceData);
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export const updateService = async (id, serviceData) => {
  try {
    const response = await api.put(`/cms/services/${id}`, serviceData);
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export const deleteService = async (id) => {
  try {
    const response = await api.delete(`/cms/services/${id}`);
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export const toggleServiceStatus = async (id) => {
  try {
    const response = await api.patch(`/cms/services/${id}/toggle`);
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

// ==================== TRAININGS APIs ====================

export const getPublicTrainings = async () => {
  try {
    const response = await api.get('/cms/trainings/public');
    return response.data;
  } catch (error) {
    console.error('Error fetching trainings:', error);
    return { data: [] };
  }
};

export const getTrainingsCMS = async () => {
  try {
    const response = await api.get('/cms/trainings');
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export const getTrainingByIdCMS = async (id) => {
  try {
    const response = await api.get(`/cms/trainings/${id}`);
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export const createTraining = async (trainingData) => {
  try {
    const response = await api.post('/cms/trainings', trainingData);
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export const updateTraining = async (id, trainingData) => {
  try {
    const response = await api.put(`/cms/trainings/${id}`, trainingData);
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export const deleteTrainingCMS = async (id) => {
  try {
    const response = await api.delete(`/cms/trainings/${id}`);
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export const toggleTrainingStatus = async (id) => {
  try {
    const response = await api.patch(`/cms/trainings/${id}/toggle`);
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

// ==================== PARTNERS APIs ====================

export const getPublicPartners = async () => {
  try {
    const response = await api.get('/cms/partners/public');
    return response.data;
  } catch (error) {
    console.error('Error fetching partners:', error);
    return { data: [] };
  }
};

export const getPartners = async () => {
  try {
    const response = await api.get('/cms/partners');
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export const getPartnerById = async (id) => {
  try {
    const response = await api.get(`/cms/partners/${id}`);
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export const createPartner = async (partnerData) => {
  try {
    const response = await api.post('/cms/partners', partnerData);
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export const updatePartner = async (id, partnerData) => {
  try {
    const response = await api.put(`/cms/partners/${id}`, partnerData);
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export const deletePartner = async (id) => {
  try {
    const response = await api.delete(`/cms/partners/${id}`);
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export const togglePartnerStatus = async (id) => {
  try {
    const response = await api.patch(`/cms/partners/${id}/toggle`);
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

// ==================== TEAM APIs ====================

export const getPublicTeam = async () => {
  try {
    const response = await api.get('/cms/team/public');
    return response.data;
  } catch (error) {
    console.error('Error fetching team:', error);
    return { data: [] };
  }
};

export const getTeamMembers = async () => {
  try {
    const response = await api.get('/cms/team');
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export const getTeamMemberById = async (id) => {
  try {
    const response = await api.get(`/cms/team/${id}`);
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export const createTeamMember = async (memberData) => {
  try {
    const response = await api.post('/cms/team', memberData);
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export const updateTeamMember = async (id, memberData) => {
  try {
    const response = await api.put(`/cms/team/${id}`, memberData);
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export const deleteTeamMember = async (id) => {
  try {
    const response = await api.delete(`/cms/team/${id}`);
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export const toggleTeamStatus = async (id) => {
  try {
    const response = await api.patch(`/cms/team/${id}/toggle`);
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

// ==================== TESTIMONIALS APIs ====================

export const getPublicTestimonials = async () => {
  try {
    const response = await api.get('/cms/testimonials/public');
    return response.data;
  } catch (error) {
    console.error('Error fetching testimonials:', error);
    return { data: [] };
  }
};

export const getTestimonials = async () => {
  try {
    const response = await api.get('/cms/testimonials');
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export const getTestimonialById = async (id) => {
  try {
    const response = await api.get(`/cms/testimonials/${id}`);
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export const createTestimonial = async (testimonialData) => {
  try {
    const response = await api.post('/cms/testimonials', testimonialData);
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export const updateTestimonial = async (id, testimonialData) => {
  try {
    const response = await api.put(`/cms/testimonials/${id}`, testimonialData);
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export const deleteTestimonial = async (id) => {
  try {
    const response = await api.delete(`/cms/testimonials/${id}`);
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export const toggleTestimonialStatus = async (id) => {
  try {
    const response = await api.patch(`/cms/testimonials/${id}/toggle`);
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

// ==================== EVENTS APIs ====================

export const getPublicEvents = async () => {
  try {
    const response = await api.get('/cms/events/public');
    return response.data;
  } catch (error) {
    console.error('Error fetching events:', error);
    return { data: [] };
  }
};

export const getEvents = async () => {
  try {
    const response = await api.get('/cms/events');
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export const getEventById = async (id) => {
  try {
    const response = await api.get(`/cms/events/${id}`);
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export const createEvent = async (eventData) => {
  try {
    const response = await api.post('/cms/events', eventData);
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export const updateEvent = async (id, eventData) => {
  try {
    const response = await api.put(`/cms/events/${id}`, eventData);
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export const deleteEvent = async (id) => {
  try {
    const response = await api.delete(`/cms/events/${id}`);
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export const toggleEventStatus = async (id) => {
  try {
    const response = await api.patch(`/cms/events/${id}/toggle`);
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

// ==================== WHY US APIs ====================

export const getPublicWhyUs = async () => {
  try {
    const response = await api.get('/cms/whyus/public');
    return response.data;
  } catch (error) {
    console.error('Error fetching why us:', error);
    return { data: [] };
  }
};

export const getWhyUsItems = async () => {
  try {
    const response = await api.get('/cms/whyus');
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export const getWhyUsById = async (id) => {
  try {
    const response = await api.get(`/cms/whyus/${id}`);
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export const createWhyUs = async (whyUsData) => {
  try {
    const response = await api.post('/cms/whyus', whyUsData);
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export const updateWhyUs = async (id, whyUsData) => {
  try {
    const response = await api.put(`/cms/whyus/${id}`, whyUsData);
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export const deleteWhyUs = async (id) => {
  try {
    const response = await api.delete(`/cms/whyus/${id}`);
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export const toggleWhyUsStatus = async (id) => {
  try {
    const response = await api.patch(`/cms/whyus/${id}/toggle`);
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

// ==================== CAREERS APIs ====================

export const getPublicCareers = async () => {
  try {
    const response = await api.get('/cms/careers/public');
    return response.data;
  } catch (error) {
    console.error('Error fetching careers:', error);
    return { data: [] };
  }
};

export const getCareers = async () => {
  try {
    const response = await api.get('/cms/careers');
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export const getCareerById = async (id) => {
  try {
    const response = await api.get(`/cms/careers/${id}`);
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export const createCareer = async (careerData) => {
  try {
    const response = await api.post('/cms/careers', careerData);
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export const updateCareer = async (id, careerData) => {
  try {
    const response = await api.put(`/cms/careers/${id}`, careerData);
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export const deleteCareer = async (id) => {
  try {
    const response = await api.delete(`/cms/careers/${id}`);
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export const toggleCareerStatus = async (id) => {
  try {
    const response = await api.patch(`/cms/careers/${id}/toggle`);
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

// ==================== GENERAL CONTENT APIs ====================

export const getContents = async () => {
  try {
    const response = await api.get('/cms/content');
    return response.data;
  } catch (error) {
    console.error('Error fetching contents:', error);
    return [];
  }
};

export const getContentById = async (id) => {
  try {
    const response = await api.get(`/cms/content/${id}`);
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export const createContent = async (contentData) => {
  try {
    const response = await api.post('/cms/content', contentData);
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export const updateContent = async (id, contentData) => {
  try {
    const response = await api.put(`/cms/content/${id}`, contentData);
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export const deleteContent = async (id) => {
  try {
    const response = await api.delete(`/cms/content/${id}`);
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export const toggleContentStatus = async (id) => {
  try {
    const response = await api.patch(`/cms/content/${id}/toggle`);
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

// ==================== MENUS APIs ====================

export const getMenus = async () => {
  try {
    const response = await api.get('/menu');
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export const getMenuById = async (id) => {
  try {
    const response = await api.get(`/menu/${id}`);
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export const createMenu = async (menuData) => {
  try {
    const response = await api.post('/menu', menuData);
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export const updateMenu = async (id, menuData) => {
  try {
    const response = await api.put(`/menu/${id}`, menuData);
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export const deleteMenu = async (id) => {
  try {
    const response = await api.delete(`/menu/${id}`);
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export const toggleMenuStatus = async (id) => {
  try {
    const response = await api.patch(`/menu/${id}/toggle`);
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

// ==================== SETTINGS APIs ====================

export const getSettings = async () => {
  try {
    const response = await api.get('/cms/settings');
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export const updateSettings = async (settings) => {
  try {
    const response = await api.put('/cms/settings', settings);
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

// ==================== ADMIN DASHBOARD APIs ====================

export const getDashboardStats = async () => {
  try {
    const response = await api.get('/admin/stats');
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

// ==================== CONTACTS MANAGEMENT APIs ====================

export const getContacts = async () => {
  try {
    const response = await api.get('/admin/contacts');
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export const getContactById = async (id) => {
  try {
    const response = await api.get(`/admin/contacts/${id}`);
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export const updateContactStatus = async (id, status) => {
  try {
    const response = await api.put(`/admin/contacts/${id}/status`, { status });
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export const deleteContact = async (id) => {
  try {
    const response = await api.delete(`/admin/contacts/${id}`);
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

// ==================== TRAINING MANAGEMENT APIs (Admin) ====================

export const getTrainings = async () => {
  try {
    const response = await api.get('/admin/trainings');
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export const getTrainingById = async (id) => {
  try {
    const response = await api.get(`/admin/trainings/${id}`);
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export const updateTrainingStatus = async (id, status) => {
  try {
    const response = await api.put(`/admin/trainings/${id}/status`, { status });
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export const deleteTraining = async (id) => {
  try {
    const response = await api.delete(`/admin/trainings/${id}`);
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

// ==================== CAREER MANAGEMENT APIs (Admin) ====================

export const getApplications = async () => {
  try {
    const response = await api.get('/admin/careers');
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export const getApplicationById = async (id) => {
  try {
    const response = await api.get(`/admin/careers/${id}`);
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export const updateApplicationStatus = async (id, status) => {
  try {
    const response = await api.put(`/admin/careers/${id}/status`, { status });
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export const deleteApplication = async (id) => {
  try {
    const response = await api.delete(`/admin/careers/${id}`);
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

// ==================== PUBLIC CMS APIs ====================

export const getPublicMenus = async () => {
  try {
    const response = await api.get('/menu/public');
    return response.data;
  } catch (error) {
    console.error('Error fetching menus:', error);
    return [];
  }
};

export const getPublicSettings = async () => {
  try {
    const response = await api.get('/cms/public/settings');
    return response.data;
  } catch (error) {
    console.error('Error fetching settings:', error);
    return {};
  }
};

export const getPublicHeader = async () => {
  try {
    const response = await api.get('/cms/public/header');
    return response.data;
  } catch (error) {
    console.error('Error fetching header:', error);
    return {};
  }
};

export const getPublicFooter = async () => {
  try {
    const response = await api.get('/cms/public/footer');
    return response.data;
  } catch (error) {
    console.error('Error fetching footer:', error);
    return {};
  }
};

export const getPublicContent = async (sectionId) => {
  try {
    const response = await api.get(`/cms/public/content/${sectionId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching content:', error);
    return {};
  }
};

export const getAllPublicContent = async () => {
  try {
    const response = await api.get('/cms/public/content');
    return response.data;
  } catch (error) {
    console.error('Error fetching all content:', error);
    return [];
  }
};

export const getBatchPublicContent = async (sectionIds) => {
  try {
    const response = await api.post('/cms/public/content/batch', { sectionIds });
    return response.data;
  } catch (error) {
    console.error('Error fetching batch content:', error);
    return {};
  }
};

export default api;